import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import {
  User,
  MultiFactorResolver,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  sendEmailVerification,
  PhoneAuthProvider,
  multiFactor,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  updateEmail,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  onAuthStateChanged,
  getIdToken,
  getMultiFactorResolver,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence
} from 'firebase/auth';
import { auth, setupRecaptcha } from '../config/firebase';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { SubscriptionData } from '../types/subscription.types';
import { calculateTrialStatus, TrialStatus } from '../utils/trialUtils';

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  userProfile: UserProfile | null;
  trialStatus: TrialStatus | null;
  signUp: (email: string, password: string, displayName: string) => Promise<void>;
  signIn: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  signInWith2FA: (verificationCode: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateUserProfile: (displayName: string, role?: string) => Promise<void>;
  updateEmailNotifications: (enabled: boolean) => Promise<void>;
  updateUserEmail: (newEmail: string, password: string) => Promise<void>;
  updateUserPassword: (newPassword: string, currentPassword: string) => Promise<void>;
  sendVerificationEmail: () => Promise<void>;
  enrollPhoneNumber: (phoneNumber: string) => Promise<void>;
  verifyPhoneNumber: (verificationCode: string) => Promise<void>;
  enable2FA: (phoneNumber: string) => Promise<void>;
  disable2FA: () => Promise<void>;
  refreshUserProfile: () => Promise<UserProfile | null>;
  getIdToken: (forceRefresh?: boolean) => Promise<string>;
  is2FAEnabled: boolean;
  phoneVerificationId: string | null;
  recaptchaVerifier: RecaptchaVerifier | null;
}

interface UserProfile {
  uid: string;
  email: string;
  displayName: string;
  role?: string;
  phoneNumber?: string;
  twoFactorEnabled: boolean;
  emailVerified: boolean;
  emailNotifications?: boolean;
  createdAt: Date;
  updatedAt: Date;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  subscription?: SubscriptionData;
  accountStatus?: 'active' | 'grace_period' | 'pending_deletion';
  gracePeriodStartDate?: Date;
  gracePeriodEndDate?: Date;
  onboardingCompleted?: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [phoneVerificationId, setPhoneVerificationId] = useState<string | null>(null);
  const [recaptchaVerifier, setRecaptchaVerifier] = useState<RecaptchaVerifier | null>(null);
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const profile = userDoc.data() as UserProfile;
          setUserProfile(profile);
          setIs2FAEnabled(profile.twoFactorEnabled);
        }
        
        const multiFactorUser = multiFactor(user);
        setIs2FAEnabled(multiFactorUser.enrolledFactors.length > 0);
      } else {
        setUserProfile(null);
        setIs2FAEnabled(false);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const createUserProfile = async (user: User, displayName: string) => {
    const userProfile: UserProfile = {
      uid: user.uid,
      email: user.email!,
      displayName: displayName,
      twoFactorEnabled: false,
      emailVerified: user.emailVerified,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    await setDoc(doc(db, 'users', user.uid), userProfile);
    setUserProfile(userProfile);
  };

  const signUp = async (email: string, password: string, displayName: string) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    
    await updateProfile(userCredential.user, { displayName });
    
    await sendEmailVerification(userCredential.user);
    
    await createUserProfile(userCredential.user, displayName);
  };

  const signIn = async (email: string, password: string, rememberMe: boolean = false) => {
    // Set persistence based on remember me option
    await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);

    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error: any) {
      if (error.code === 'auth/multi-factor-auth-required') {
        // Resolve the MFA challenge
        const resolver = getMultiFactorResolver(auth, error);
        setMfaResolver(resolver);
        setIs2FAEnabled(true);

        // Send SMS verification code using the first enrolled factor
        const phoneHint = resolver.hints[0];
        const verifier = recaptchaVerifier || setupRecaptcha(auth, 'recaptcha-container');
        if (!recaptchaVerifier) {
          setRecaptchaVerifier(verifier);
        }

        const phoneAuthProvider = new PhoneAuthProvider(auth);
        const verificationId = await phoneAuthProvider.verifyPhoneNumber(
          { multiFactorHint: phoneHint, session: resolver.session },
          verifier
        );
        setPhoneVerificationId(verificationId);

        throw new Error('2FA_REQUIRED');
      }
      throw error;
    }
  };

  const signInWith2FA = async (verificationCode: string) => {
    if (!phoneVerificationId || !mfaResolver) {
      throw new Error('No verification session found');
    }

    const credential = PhoneAuthProvider.credential(phoneVerificationId, verificationCode);
    const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(credential);
    await mfaResolver.resolveSignIn(multiFactorAssertion);
    setMfaResolver(null);
  };

  const logout = async () => {
    await signOut(auth);
    setCurrentUser(null);
    setUserProfile(null);
    setIs2FAEnabled(false);
  };

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  const updateUserProfile = async (displayName: string, role?: string) => {
    if (!currentUser) throw new Error('No user logged in');

    await updateProfile(currentUser, { displayName });

    if (userProfile) {
      const userDocRef = doc(db, 'users', currentUser.uid);
      const updateData: any = {
        displayName,
        updatedAt: new Date()
      };

      if (role !== undefined) {
        updateData.role = role;
      }

      await updateDoc(userDocRef, updateData);

      setUserProfile({
        ...userProfile,
        displayName,
        ...(role !== undefined && { role }),
        updatedAt: new Date()
      });
    }
  };

  const updateEmailNotifications = async (enabled: boolean) => {
    if (!currentUser) throw new Error('No user logged in');

    if (userProfile) {
      // Call backend API to sync with Mailgun suppression list
      const api = (await import('../services/api')).default;
      await api.put('/api/auth/email-preferences', { emailNotifications: enabled });

      // Update Firestore
      const userDocRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userDocRef, {
        emailNotifications: enabled,
        updatedAt: new Date()
      });

      setUserProfile({
        ...userProfile,
        emailNotifications: enabled,
        updatedAt: new Date()
      });
    }
  };

  const updateUserEmail = async (newEmail: string, password: string) => {
    if (!currentUser || !currentUser.email) throw new Error('No user logged in');
    
    const credential = EmailAuthProvider.credential(currentUser.email, password);
    await reauthenticateWithCredential(currentUser, credential);
    
    await updateEmail(currentUser, newEmail);
    
    await sendEmailVerification(currentUser);
    
    if (userProfile) {
      const userDocRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userDocRef, {
        email: newEmail,
        emailVerified: false,
        updatedAt: new Date()
      });
    }
  };

  const updateUserPassword = async (newPassword: string, currentPassword: string) => {
    if (!currentUser || !currentUser.email) throw new Error('No user logged in');
    
    const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
    await reauthenticateWithCredential(currentUser, credential);
    
    await updatePassword(currentUser, newPassword);
  };

  const sendVerificationEmail = async () => {
    if (!currentUser) throw new Error('No user logged in');
    await sendEmailVerification(currentUser);
  };

  const enrollPhoneNumber = async (phoneNumber: string) => {
    if (!currentUser) throw new Error('No user logged in');
    
    if (!recaptchaVerifier) {
      const verifier = setupRecaptcha(auth, 'recaptcha-container');
      setRecaptchaVerifier(verifier);
    }
    
    const multiFactorUser = multiFactor(currentUser);
    const session = await multiFactorUser.getSession();
    
    const phoneInfoOptions = {
      phoneNumber,
      session
    };
    
    const phoneAuthProvider = new PhoneAuthProvider(auth);
    const verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier!);
    setPhoneVerificationId(verificationId);
  };

  const verifyPhoneNumber = async (verificationCode: string) => {
    if (!phoneVerificationId) {
      throw new Error('No verification ID found');
    }
    
    if (!currentUser) throw new Error('No user logged in');
    
    const credential = PhoneAuthProvider.credential(phoneVerificationId, verificationCode);
    
    const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(credential);
    const multiFactorUser = multiFactor(currentUser);
    
    await multiFactorUser.enroll(multiFactorAssertion, 'Phone Number');
  };

  const enable2FA = async (phoneNumber: string) => {
    if (!currentUser) throw new Error('No user logged in');
    
    await enrollPhoneNumber(phoneNumber);
    
    if (userProfile) {
      const userDocRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userDocRef, {
        phoneNumber,
        twoFactorEnabled: true,
        updatedAt: new Date()
      });
      
      setUserProfile({
        ...userProfile,
        phoneNumber,
        twoFactorEnabled: true,
        updatedAt: new Date()
      });
      setIs2FAEnabled(true);
    }
  };

  const disable2FA = async () => {
    if (!currentUser) throw new Error('No user logged in');
    
    const multiFactorUser = multiFactor(currentUser);
    
    for (const factor of multiFactorUser.enrolledFactors) {
      await multiFactorUser.unenroll(factor);
    }
    
    if (userProfile) {
      const userDocRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userDocRef, {
        twoFactorEnabled: false,
        phoneNumber: null,
        updatedAt: new Date()
      });
      
      setUserProfile({
        ...userProfile,
        twoFactorEnabled: false,
        phoneNumber: undefined,
        updatedAt: new Date()
      });
      setIs2FAEnabled(false);
    }
  };

  const refreshUserProfile = async (): Promise<UserProfile | null> => {
    if (!currentUser) throw new Error('No user logged in');

    const userDocRef = doc(db, 'users', currentUser.uid);
    const userDoc = await getDoc(userDocRef);

    if (userDoc.exists()) {
      const profile = userDoc.data() as UserProfile;
      setUserProfile(profile);
      setIs2FAEnabled(profile.twoFactorEnabled);
      return profile;
    }
    return null;
  };

  const getIdTokenWrapper = async (forceRefresh = false): Promise<string> => {
    if (!currentUser) throw new Error('No user logged in');
    return await getIdToken(currentUser, forceRefresh);
  };

  // Calculate trial status from user profile
  const trialStatus = useMemo(() => {
    if (!userProfile) return null;
    return calculateTrialStatus(
      userProfile.subscription?.planName,
      userProfile.createdAt
    );
  }, [userProfile]);

  const value: AuthContextType = {
    currentUser,
    loading,
    userProfile,
    trialStatus,
    signUp,
    signIn,
    signInWith2FA,
    logout,
    resetPassword,
    updateUserProfile,
    updateEmailNotifications,
    updateUserEmail,
    updateUserPassword,
    sendVerificationEmail,
    enrollPhoneNumber,
    verifyPhoneNumber,
    enable2FA,
    disable2FA,
    refreshUserProfile,
    getIdToken: getIdTokenWrapper,
    is2FAEnabled,
    phoneVerificationId,
    recaptchaVerifier
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};